import { CikisConfig, CikisTipi } from '../FikoSPHParametreler';
import { ISPHEleman, ElemanTipi, olusturId } from './ISPHEleman';
import { Vector3 } from 'three';

/**
 * FikoCikis - Outlet/drain element for SPH fluid simulation
 * GPU struct size: 64 bytes
 *
 * Layout:
 * - pozX, pozY, pozZ, cap (16 bytes)
 * - yonX, yonY, yonZ, tipi as u32 (16 bytes - tipi in w slot)
 * - hedefBasinc, hedefSeviye, aktif as u32, acilmaZamani (16 bytes)
 * - pad x4 (16 bytes)
 */
export class FikoCikis implements ISPHEleman {
  readonly id: string;
  readonly tip: ElemanTipi = ElemanTipi.CIKIS;

  pozisyon: Vector3;
  yon: Vector3;
  cap: number;
  tipi: CikisTipi;
  hedefBasinc: number;
  hedefSeviye: number;
  aktif: boolean;
  acilmaZamani: number;
  ad: string;

  constructor(config: Partial<CikisConfig> = {}) {
    this.id = config.id || olusturId();

    this.pozisyon = config.pozisyon ? new Vector3(config.pozisyon.x, config.pozisyon.y, config.pozisyon.z) : new Vector3(0, 0, 0);
    this.yon = config.yon ? new Vector3(config.yon.x, config.yon.y, config.yon.z).normalize() : new Vector3(0, -1, 0);
    this.cap = config.cap ?? 0.01;
    this.tipi = config.tipi ?? CikisTipi.SERBEST;
    this.hedefBasinc = config.hedefBasinc ?? 0;
    this.hedefSeviye = config.hedefSeviye ?? 0;
    this.aktif = config.aktif ?? false;
    this.acilmaZamani = config.acilmaZamani ?? 0;
    this.ad = config.ad || 'Çıkış';
  }

  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(64);
    const view = new DataView(buffer);
    let offset = 0;

    // Chunk 1: pozX, pozY, pozZ, cap (16 bytes)
    view.setFloat32(offset, this.pozisyon.x, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.y, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.z, true); offset += 4;
    view.setFloat32(offset, this.cap, true); offset += 4;

    // Chunk 2: yonX, yonY, yonZ, tipi as u32 (16 bytes)
    view.setFloat32(offset, this.yon.x, true); offset += 4;
    view.setFloat32(offset, this.yon.y, true); offset += 4;
    view.setFloat32(offset, this.yon.z, true); offset += 4;
    // Map string enum to numeric for GPU
    const tipiNumeric = this.tipi === CikisTipi.SERBEST ? 0 : this.tipi === CikisTipi.BASINC_KONTROL ? 1 : 2;
    view.setUint32(offset, tipiNumeric, true); offset += 4;

    // Chunk 3: hedefBasinc, hedefSeviye, aktif as u32, acilmaZamani (16 bytes)
    view.setFloat32(offset, this.hedefBasinc, true); offset += 4;
    view.setFloat32(offset, this.hedefSeviye, true); offset += 4;
    view.setUint32(offset, this.aktif ? 1 : 0, true); offset += 4;
    view.setFloat32(offset, this.acilmaZamani, true); offset += 4;

    // Chunk 4: pad x4 (16 bytes)
    for (let i = 0; i < 4; i++) {
      view.setUint32(offset, 0, true);
      offset += 4;
    }

    return buffer;
  }

  gpuStructBoyut(): number {
    return 64;
  }

  toJSON(): any {
    return {
      id: this.id,
      tip: ElemanTipi[this.tip],
      pozisyon: { x: this.pozisyon.x, y: this.pozisyon.y, z: this.pozisyon.z },
      yon: { x: this.yon.x, y: this.yon.y, z: this.yon.z },
      cap: this.cap,
      tipi: CikisTipi[this.tipi],
      hedefBasinc: this.hedefBasinc,
      hedefSeviye: this.hedefSeviye,
      aktif: this.aktif,
      acilmaZamani: this.acilmaZamani,
      ad: this.ad
    };
  }

  dogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.cap <= 0) {
      hatalar.push('Çap pozitif olmalıdır');
    }

    if (this.yon.lengthSq() < 0.99 || this.yon.lengthSq() > 1.01) {
      hatalar.push('Yön vektörü normalize edilmeli');
    }

    if (this.acilmaZamani < 0) {
      hatalar.push('Açılma zamanı negatif olamaz');
    }

    // Type-specific validation
    switch (this.tipi) {
      case CikisTipi.BASINC_KONTROL:
        if (this.hedefBasinc < 0) {
          hatalar.push('Hedef basınç negatif olamaz');
        }
        if (this.hedefBasinc === 0) {
          hatalar.push('Basınç kontrollü çıkış için hedef basınç belirtilmeli');
        }
        break;

      case CikisTipi.SEVIYE_KONTROL:
        if (this.hedefSeviye === 0) {
          hatalar.push('Seviye kontrollü çıkış için hedef seviye belirtilmeli');
        }
        break;

      case CikisTipi.SERBEST:
        // No additional validation for free outlet
        break;
    }

    return {
      gecerli: hatalar.length === 0,
      hatalar
    };
  }

  klonla(): FikoCikis {
    return new FikoCikis({
      pozisyon: this.pozisyon.clone(),
      yon: this.yon.clone(),
      cap: this.cap,
      tipi: this.tipi,
      hedefBasinc: this.hedefBasinc,
      hedefSeviye: this.hedefSeviye,
      aktif: this.aktif,
      acilmaZamani: this.acilmaZamani,
      ad: this.ad + ' (Kopya)'
    });
  }

  parametreListesi(): string[] {
    return [
      'pozisyon', 'yon', 'cap', 'tipi', 'hedefBasinc',
      'hedefSeviye', 'aktif', 'acilmaZamani', 'ad'
    ];
  }

  parametreAyarla(anahtar: string, deger: any): boolean {
    switch (anahtar) {
      case 'pozisyon':
        if (deger instanceof Vector3) {
          this.pozisyon.copy(deger);
          return true;
        }
        break;
      case 'yon':
        if (deger instanceof Vector3) {
          this.yon.copy(deger).normalize();
          return true;
        }
        break;
      case 'cap':
      case 'hedefBasinc':
      case 'hedefSeviye':
      case 'acilmaZamani':
        if (typeof deger === 'number') {
          (this as any)[anahtar] = deger;
          return true;
        }
        break;
      case 'tipi':
        // Accept both string and number for enum assignment
        (this as any).tipi = deger;
        return true;
      case 'aktif':
        if (typeof deger === 'boolean') {
          this.aktif = deger;
          return true;
        }
        break;
      case 'ad':
        if (typeof deger === 'string') {
          this.ad = deger;
          return true;
        }
        break;
    }
    return false;
  }

  /**
   * Get a parameter value by name
   */
  parametreAl(anahtar: string): any {
    return (this as any)[anahtar];
  }

  /**
   * Activate the outlet at specified time
   */
  aktifEt(zaman: number = 0): void {
    this.aktif = true;
    this.acilmaZamani = zaman;
  }

  /**
   * Deactivate the outlet
   */
  deaktifEt(): void {
    this.aktif = false;
  }

  /**
   * Check if outlet should be active at given simulation time
   */
  zamanindaAktifMi(simulasyonZamani: number): boolean {
    return this.aktif && simulasyonZamani >= this.acilmaZamani;
  }

  /**
   * Calculate flow rate based on outlet type
   * (Simplified calculation - actual flow computed in GPU shader)
   */
  akisHiziTahmin(basinc: number, seviye: number): number {
    if (!this.aktif) return 0;

    const alan = Math.PI * this.cap * this.cap / 4;

    switch (this.tipi) {
      case CikisTipi.SERBEST:
        // Free drainage based on gravity
        return alan * Math.sqrt(2 * 9.81 * Math.max(0, seviye - this.pozisyon.y));

      case CikisTipi.BASINC_KONTROL:
        // Pressure-controlled outlet
        const basincFarki = Math.max(0, basinc - this.hedefBasinc);
        return alan * Math.sqrt(2 * basincFarki / 1000); // Assuming water density

      case CikisTipi.SEVIYE_KONTROL:
        // Level-controlled outlet (on/off based on level)
        return seviye > this.hedefSeviye ? alan * 0.1 : 0;

      default:
        return 0;
    }
  }
}

/**
 * Factory function to create default outlet element
 */
export function varsayilanCikis(config?: Partial<CikisConfig>): FikoCikis {
  return new FikoCikis(config);
}
